/// <reference types="google.maps" />
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { Logger } from '@sondermind/datadog-logging';
import { ILatLon } from '@sondermind/data-access/public-facing/filter-form';

import { GOOGLE_MAPS_API_KEY } from '../shared/constants';

const API_ENDPOINT = 'https://maps.googleapis.com/maps/api/geocode/json';
const TIMEZONE_API_ENDPOINT = 'https://maps.googleapis.com/maps/api/timezone/json';
const MILLIS_PER_SECOND = 1000;

/**
 * `HttpErrorResponse.status` when no HTTP response was received: the request was
 * cancelled client-side, or a network/CORS/DNS/TLS failure occurred. This is not
 * a Google Maps API error (no 4xx/5xx was returned).
 */
const NO_RESPONSE_STATUS = 0;

/** Structured, log-safe fields extracted from a geocode request failure. */
interface IGeocodeErrorContext {
  status: number | null;
  statusText: string | null;
  message: string;
}

interface IGeocodeJsonResponse {
  status: string;
  results?: google.maps.GeocoderResult[];
}

interface ITimeZoneJsonResponse {
  status: string;
  timeZoneId?: string;
  timeZoneName?: string;
  errorMessage?: string;
}

export interface ICityLatLng {
  lat: number;
  lng: number;
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsGeocodeService {
  private readonly cityStateCache = new Map<string, ICityLatLng | null>();
  private readonly cityStateInFlight = new Map<string, Observable<ICityLatLng | null>>();
  private readonly logger = inject(Logger, { optional: true });

  constructor(private httpClient: HttpClient) {}

  /**
   * Resolve the IANA time zone id (e.g. `America/New_York`) for a coordinate via the Google
   * Time Zone API. Returns `null` if the lookup fails or the API returns a non-OK status, so
   * callers can fall back to the existing zone rather than persisting a bad value.
   *
   * @param lat latitude of the location
   * @param lng longitude of the location
   */
  getTimeZoneId$(lat: number, lng: number): Observable<string | null> {
    const timestampSeconds = Math.floor(Date.now() / MILLIS_PER_SECOND);
    const url = `${TIMEZONE_API_ENDPOINT}?location=${lat},${lng}`
      + `&timestamp=${timestampSeconds}`
      + `&key=${GOOGLE_MAPS_API_KEY}`;

    return this.httpClient.get<ITimeZoneJsonResponse>(url).pipe(
      map((response) => {
        if (response.status !== 'OK' || !response.timeZoneId) {
          this.logger?.warn('GoogleMapsGeocodeService: time zone lookup returned no zone', {
            timeZoneStatus: response.status,
            errorMessage: response.errorMessage,
            lat,
            lng,
          });
          return null;
        }
        return response.timeZoneId;
      }),
      catchError((error: unknown) => {
        this.logGeocodeFailure('time zone lookup request', error, { lat, lng });
        return of(null);
      })
    );
  }

  reverseGeocode(lat: number, lng: number): Observable<google.maps.GeocoderResult[]> {
    const url = `${API_ENDPOINT}?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`;
    return this.httpClient.get<google.maps.GeocoderResponse>(url).pipe(
      map((response: google.maps.GeocoderResponse) => response.results)
    );
  }

  geocodeCityState$(city: string, state: string): Observable<ICityLatLng | null> {
    const c = city?.trim() ?? '';
    const s = state?.trim() ?? '';
    if (!c || !s) {
      return of(null);
    }

    const key = this.cityStateCacheKey(c, s);
    const cached = this.cityStateCache.get(key);
    if (cached !== undefined) {
      return of(cached);
    }

    const existing = this.cityStateInFlight.get(key);
    if (existing) {
      return existing;
    }

    const request$ = this.createGeocodeRequest$(c, s).pipe(
      tap((result) => {
        this.cityStateCache.set(key, result);
      }),
      finalize(() => {
        this.cityStateInFlight.delete(key);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cityStateInFlight.set(key, request$);
    return request$;
  }

  forwardGeocode(address: string): Observable<ILatLon | null> {
    // Restrict the geocode to a US postal_code component (+region bias). A bare free-text
    // ?address=<zip> makes Google return ZERO_RESULTS (HTTP 200) for some valid US ZIPs
    // (e.g. 90210), which surfaced as a false "Zip code not found". MER-5125.
    const params = {
      address,
      components: `postal_code:${address}|country:US`,
      region: 'us',
      key: GOOGLE_MAPS_API_KEY,
    };
    return this.httpClient.get<IGeocodeJsonResponse>(API_ENDPOINT, { params }).pipe(
      map((response) => {
        // Distinguish between "no results" (expected) and service errors (unexpected)
        if (response.status === 'ZERO_RESULTS') {
          // Expected case: valid zip code but no geocoding results
          return null;
        }

        if (response.status !== 'OK') {
          // Service error: quota exhaustion, bad API key, malformed request, etc.
          this.logger?.warn('Geocode service error - non-OK status', {
            status: response.status,
            address
          });
          throw new Error(`Geocoding failed: ${response.status}`);
        }

        const result = this.extractLatLng(response);
        if (!result) return null;
        return { lat: result.lat, lon: result.lng };
      }),
      catchError((error: unknown) => {
        this.logGeocodeFailure('forward geocode request', error, { address });
        // Re-throw infrastructure failures (network error, 4xx/5xx, quota) so callers can
        // distinguish them from a successful "no results" response (which returns null above).
        // Swallowing here would make an outage indistinguishable from an invalid zip.
        return throwError(() => error);
      })
    );
  }

  private cityStateCacheKey(city: string, state: string): string {
    return `${city.trim().toLowerCase()}|${state.trim().toLowerCase()}`;
  }

  private createGeocodeRequest$(city: string, state: string): Observable<ICityLatLng | null> {
    const address = `${city}, ${state}, USA`;
    const url = `${API_ENDPOINT}?address=${encodeURIComponent(address)}&region=us&key=${GOOGLE_MAPS_API_KEY}`;

    return this.httpClient.get<IGeocodeJsonResponse>(url).pipe(
      map((response) => {
        const result = this.extractLatLng(response);
        if (result === null) {
          this.logger?.warn('GoogleMapsGeocodeService: city/state geocode returned no coordinates', {
            geocodeStatus: response.status,
            city,
            state,
            resultCount: response.results?.length ?? 0,
          });
        }
        return result;
      }),
      catchError((error: unknown) => {
        this.logGeocodeFailure('city/state geocode request', error, { city, state });
        return of(null);
      })
    );
  }

  /**
   * Log a geocode/time-zone request failure with structured, log-safe fields.
   *
   * An `HttpErrorResponse` is not an `Error` instance and stringifies to the useless
   * `"[object Object]"`, so `status`/`statusText`/`message` are pulled out explicitly.
   *
   * No-response failures (`status: 0` — the request was cancelled client-side or a
   * network/CORS/DNS/TLS failure occurred, not a Maps API error) are logged at `debug`
   * rather than `error`: on the public provider directory these are overwhelmingly
   * bot-driven page-lifecycle aborts and were polluting error/emergency severity.
   * Real Maps API failures (4xx/5xx) and non-HTTP errors stay at `error`. See MER-4854.
   *
   * @param operation short description of the failed request (e.g. `city/state geocode request`)
   * @param error the caught error
   * @param context extra structured fields to include in the log entry
   */
  private logGeocodeFailure(operation: string, error: unknown, context: Record<string, unknown>): void {
    const logContext = { ...context, ...this.toGeocodeErrorContext(error) };
    if (logContext.status === NO_RESPONSE_STATUS) {
      this.logger?.debug(`GoogleMapsGeocodeService: ${operation} received no response`, logContext);
    } else {
      this.logger?.error(`GoogleMapsGeocodeService: ${operation} failed`, logContext);
    }
  }

  /**
   * Extract structured, log-safe fields from a geocode request failure. An
   * `HttpErrorResponse` is not an `Error` instance and stringifies to the useless
   * `"[object Object]"`, so pull `status`/`statusText`/`message` out explicitly. The
   * API key is redacted from the message because `HttpErrorResponse.message` embeds
   * the request URL, which carries the key as a query param.
   */
  private toGeocodeErrorContext(error: unknown): IGeocodeErrorContext {
    if (error instanceof HttpErrorResponse) {
      return {
        status: error.status,
        statusText: error.statusText,
        message: this.redactApiKey(error.message),
      };
    }
    return {
      status: null,
      statusText: null,
      message: this.redactApiKey(error instanceof Error ? error.message : String(error)),
    };
  }

  /** Redact the Google Maps API key from a string before it is logged. */
  private redactApiKey(message: string): string {
    if (!GOOGLE_MAPS_API_KEY) {
      return message;
    }
    return message.split(GOOGLE_MAPS_API_KEY).join('[REDACTED]');
  }

  private extractLatLng(response: IGeocodeJsonResponse): ICityLatLng | null {
    if (response.status !== 'OK' || !response.results?.length) {
      return null;
    }

    // Geocode JSON returns lat/lng numbers; @types/google.maps types geometry.location as LatLng (methods).
    const location = response.results[0]?.geometry?.location as unknown;
    if (
      location == null ||
      typeof location !== 'object' ||
      !('lat' in location) ||
      !('lng' in location)
    ) {
      return null;
    }
    const loc = location as { lat: unknown; lng: unknown; };
    if (typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
      return null;
    }

    return { lat: loc.lat, lng: loc.lng };
  }
}
